import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../utils/constant";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Skeleton,
} from "@mui/material";

// Skeleton data for the loading state
const skeletonData = Array(12)
  .fill()
  .map((_, i) => ({
    month: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][i],
    currentYear: Math.random() * 1000,
    lastYear: Math.random() * 1000,
  }));

const LoadingSkeleton = () => (
  <Card sx={{ maxWidth: 800, mx: "auto", mb: 3, p: 2 }}>
    <CardContent>
      <Skeleton variant="text" width={200} height={40} />
      <Box sx={{ height: 310, mt: 2 }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    </CardContent>
  </Card>
);

const ErrorState = ({ error }) => (
  <Card sx={{ maxWidth: 800, mx: "auto", mb: 3, p: 2 }}>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Unable to load cash flow data
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    </CardContent>
  </Card>
);

const CashFlowGraph = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const organizationId = JSON.parse(
          localStorage.getItem("organization")
        )?.id;

        // Using the new API endpoint
        const response = await fetch(
          `${BASE_URL}/org/organization/${organizationId}/transactions/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        
        const result = await response.json();
        
        if (!result.status) {
          throw new Error(result.message || "Failed to fetch transaction data");
        }

        const transactions = result.data || [];
        
        // Process transaction data for chart
        const monthlyTotals = {
          currentYear: {},
          lastYear: {},
        };

        const currentYear = new Date().getFullYear();
        const lastYear = currentYear - 1;

        transactions.forEach((transaction) => {
          const transactionDate = transaction.transaction_date;
          if (!transactionDate) return;

          const date = new Date(transactionDate);
          if (isNaN(date.getTime())) return;

          const year = date.getFullYear();
          if (year !== currentYear && year !== lastYear) return;

          const month = date.toLocaleString("default", { month: "short" });
          let amount = 0;

          // Handle different transaction types
          if (transaction.transaction_type === "vendor_material") {
            amount = transaction.total_price || 0;
          } else {
            amount = transaction.amount || 0;
          }

          // Check if it's an expense category (if needed)
          // This assumes that certain categories are expenses that should be negative
          // You may need to adjust this logic based on your specific categories
          if (transaction.category === "Expense") {
            amount = -amount;
          }

          if (isNaN(amount)) return;

          const yearKey = year === currentYear ? "currentYear" : "lastYear";
          monthlyTotals[yearKey][month] =
            (monthlyTotals[yearKey][month] || 0) + amount;
        });

        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        const chartData = months.map((month) => ({
          month,
          currentYear: monthlyTotals.currentYear[month] || 0,
          lastYear: monthlyTotals.lastYear[month] || 0,
        }));

        setMonthlyData(chartData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching transaction data:", error);
        setError(error.message || "Failed to load transaction data");
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <Card sx={{ maxWidth: 800, mx: "auto", mb: 3, p: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Total Cash Flow
        </Typography>
        <Box sx={{ height: 310 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={monthlyData}
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
            >
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#666", fontSize: 12 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#666", fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                dx={-10}
                padding={{ top: 20, bottom: 20 }}
              />
              <Tooltip
                formatter={(value) => [`₹${value.toFixed(2)}`, ""]}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                offset={20}
              />
              <Legend
                align="left"
                verticalAlign="top"
                iconType="circle"
                wrapperStyle={{ paddingBottom: "20px", fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="currentYear"
                name="This year"
                stroke="#3B0069"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="lastYear"
                name="Last year"
                stroke="gray"
                opacity={0.7}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CashFlowGraph;